import React, { useState } from 'react';
import './DebugPage.css';
import EffectsOverlay from './EffectsOverlay';
import DiceSvg from './DiceSvg';
import { 
  playMeowExcited, 
  playMeowHappy, 
  playPurrContent, 
  playPurrRoll, 
  playPurrLoud, 
  playMeowSurprise
} from '../utils/audioUtils';

const DebugPage = ({ backgroundTheme, setBackgroundTheme, backgroundThemes }) => {
  const [currentEffect, setCurrentEffect] = useState(null);
  
  // Dice prototype states
  const [prototype1Dice, setPrototype1Dice] = useState([3, 1, 4, 6, 2]);
  const [prototype1Held, setPrototype1Held] = useState([false, true, false, true, false]);
  const [prototype1Rolls, setPrototype1Rolls] = useState(2);
  
  const [prototype2Dice, setPrototype2Dice] = useState([3, 1, 4, 6, 2]);
  const [prototype2Held, setPrototype2Held] = useState([false, true, false, true, false]);
  const [prototype2Rolls, setPrototype2Rolls] = useState(2);
  
  const [prototype3Dice, setPrototype3Dice] = useState([3, 1, 4, 6, 2]);
  const [prototype3Held, setPrototype3Held] = useState([false, true, false, true, false]);
  const [prototype3Rolls, setPrototype3Rolls] = useState(2);
  
  const [linearDice, setLinearDice] = useState([5, null, 2, 4, 1]);
  const [linearHeld, setLinearHeld] = useState([true, false, false, true, false]);
  const [linearRolls, setLinearRolls] = useState(1);
  
  const [cardDice, setCardDice] = useState([6, 6, 4, 4, 4]);
  const [cardHeld, setCardHeld] = useState([false, false, true, true, true]);
  const [cardRolls, setCardRolls] = useState(2);

  const handleEffectComplete = () => {
    setCurrentEffect(null);
  };

  // Helper functions for dice prototypes
  const rollDice = () => Math.floor(Math.random() * 6) + 1;
  
  const rollPrototypeDice = (dice, held, setDice, setRolls, rollsLeft) => {
    if (rollsLeft > 0) {
      playMeowHappy();
      const newDice = dice.map((die, index) => 
        held[index] ? die : (die === null ? rollDice() : rollDice())
      );
      setDice(newDice);
      setRolls(rollsLeft - 1);
    }
  };
  
  const toggleHold = (index, held, setHeld, rollsLeft) => {
    if (rollsLeft < 3) {
      playPurrContent();
      const newHeld = [...held];
      newHeld[index] = !newHeld[index];
      setHeld(newHeld);
    }
  };
  
  const resetPrototype = (setDice, setHeld, setRolls, initialDice, initialHeld) => {
    setDice([...initialDice]);
    setHeld([...initialHeld]);
    setRolls(3);
  };

  const triggerEffect = (effectType) => {
    setCurrentEffect(effectType);
  };

  const triggerScreenEffect = (className) => {
    document.body.classList.add(className);
    setTimeout(() => {
      document.body.classList.remove(className);
    }, 2500);
  };

  const triggerExplosion = () => {
    const catTransformations = [
      '/cat-images/cat-fly-1.png',
      '/cat-images/cat-fly-2.webp', 
      '/cat-images/cat-fly-3.png',
      '/cat-images/cat-surprise.png',
      '/cat-images/cat-high-5.png'
    ];

    const explosionDiv = document.createElement('div');
    explosionDiv.className = 'roll-explosion';
    explosionDiv.innerHTML = Array.from({ length: 20 }, (_, i) => 
      `<img src="${catTransformations[i % catTransformations.length]}" class="explosion-cat explosion-${i % 10}" style="--random-x: ${Math.random()}; --random-y: ${Math.random()};" alt="Exploding cat" />`
    ).join('');
    document.body.appendChild(explosionDiv);
    
    setTimeout(() => {
      if (explosionDiv.parentNode) {
        explosionDiv.parentNode.removeChild(explosionDiv);
      }
    }, 3000);
  };

  const effectButtons = [
    {
      name: 'Yahtzee Effect',
      effect: { type: 'yahtzee' },
      sound: playMeowExcited,
      description: 'Special yahtzee celebration'
    },
    {
      name: 'Achievement',
      effect: { type: 'achievement', description: 'Score Master - 300+ Points!' },
      sound: null,
      description: 'Score achievement popup'
    }
  ];

  const threeOfAKindButtons = [
    {
      name: 'Cat Clone Army',
      effect: { type: 'kind', count: 3, emoji: '⚃', variant: 0 },
      sound: playMeowHappy,
      description: 'Military cats with dice fortress and explosions'
    },
    {
      name: 'Magic Mirror Maze',
      effect: { type: 'kind', count: 3, emoji: '⚃', variant: 1 },
      sound: playMeowHappy,
      description: 'Floating mirrors with crystal pedestals and portals'
    },
    {
      name: 'Triple Trouble',
      effect: { type: 'kind', count: 3, emoji: '⚃', variant: 2 },
      sound: playMeowHappy,
      description: 'Mischievous cats with dice tornado and pranks'
    },
    {
      name: 'Random 3 of a Kind',
      effect: { type: 'kind', count: 3, emoji: '⚃' },
      sound: playMeowHappy,
      description: 'Cycles through all 3-of-a-kind variants'
    }
  ];

  const fourOfAKindButtons = [
    {
      name: 'Cat Carnival Extravaganza',
      effect: { type: 'kind', count: 4, emoji: '⚃', variant: 3 },
      sound: playPurrContent,
      description: 'Ferris wheel cats with circus tents and roller coasters'
    },
    {
      name: 'Elemental Cat Force',
      effect: { type: 'kind', count: 4, emoji: '⚃', variant: 4 },
      sound: playPurrContent,
      description: 'Fire, water, earth, air cats with convergence'
    },
    {
      name: 'Cyber Cat Matrix',
      effect: { type: 'kind', count: 4, emoji: '⚃', variant: 5 },
      sound: playPurrContent,
      description: 'Hacker cats with holographic cubes and code rain'
    },
    {
      name: 'Random 4 of a Kind',
      effect: { type: 'kind', count: 4, emoji: '⚃' },
      sound: playPurrContent,
      description: 'Cycles through all 4-of-a-kind variants'
    }
  ];

  const fullHouseButtons = [
    {
      name: 'Cat Mansion Builder',
      effect: { type: 'fullhouse', variant: 0 },
      sound: playPurrLoud,
      description: 'Architect cats building rainbow mansion'
    },
    {
      name: 'Royal Cat Palace',
      effect: { type: 'fullhouse', variant: 1 },
      sound: playPurrLoud,
      description: 'Royal cats with golden palace'
    },
    {
      name: 'Cat City Metropolis',
      effect: { type: 'fullhouse', variant: 2 },
      sound: playPurrLoud,
      description: 'Skyscraper city with helicopter cats'
    },
    {
      name: 'Random Full House',
      effect: { type: 'fullhouse' },
      sound: playPurrLoud,
      description: 'Cycles through all variants'
    }
  ];

  const smallStraightButtons = [
    {
      name: 'Neon Racetrack',
      effect: { type: 'straight', variant: 0 },
      sound: playMeowSurprise,
      description: 'Racing cats with synthwave grid'
    },
    {
      name: 'Cat Surf Pipeline',
      effect: { type: 'straight', variant: 1 },
      sound: playMeowSurprise,
      description: 'Surfing cats with tropical vibes'
    },
    {
      name: 'Random Small Straight',
      effect: { type: 'straight', size: 'small' },
      sound: playMeowSurprise,
      description: 'Cycles through small straight variants'
    }
  ];

  const largeStraightButtons = [
    {
      name: 'Space Race Odyssey',
      effect: { type: 'straight', variant: 2 },
      sound: playMeowSurprise,
      description: 'Astronaut cats with galaxy spiral'
    },
    {
      name: 'Time Warp Portal',
      effect: { type: 'straight', variant: 3 },
      sound: playMeowSurprise,
      description: 'Time traveling cats with clock gears'
    },
    {
      name: 'Random Large Straight',
      effect: { type: 'straight', size: 'large' },
      sound: playMeowSurprise,
      description: 'Cycles through large straight variants'
    }
  ];

  const screenEffects = [
    {
      name: 'Screen Shake',
      className: 'screen-shake',
      description: 'Basic screen shake'
    },
    {
      name: 'Roll Chaos',
      className: 'roll-chaos',
      description: 'Rotation and scaling'
    },
    {
      name: 'Rainbow Flash',
      className: 'rainbow-flash',
      description: 'Color cycling'
    },
    {
      name: 'Mega Screen Shake',
      className: 'mega-screen-shake',
      description: 'Intense shaking'
    },
    {
      name: 'Ultra Chaos',
      className: 'ultra-chaos',
      description: 'Maximum chaos mode'
    },
    {
      name: 'Rainbow Overload',
      className: 'rainbow-overload',
      description: 'Intense color effects'
    },
    {
      name: 'Color Seizure',
      className: 'color-seizure',
      description: 'Background color cycling'
    }
  ];

  const audioEffects = [
    { name: 'Meow Excited', sound: playMeowExcited },
    { name: 'Meow Happy', sound: playMeowHappy },
    { name: 'Meow Surprise', sound: playMeowSurprise },
    { name: 'Purr Content', sound: playPurrContent },
    { name: 'Purr Roll', sound: playPurrRoll },
    { name: 'Purr Loud', sound: playPurrLoud }
  ];

  const currentTheme = backgroundThemes?.[backgroundTheme] || backgroundThemes?.default;

  return (
    <div className="debug-page" style={{ background: currentTheme?.backgroundColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Background preview */}
      {currentTheme && (
        <div className={`floating-madness theme-${backgroundTheme}`}>
          {/* Cats */}
          {Array.from({ length: 20 }, (_, i) => (
            <img 
              key={`cat-${i}`}
              src={currentTheme.cats[i % currentTheme.cats.length]}
              alt="Theme cat"
              className={`madness-cat cat-${currentTheme.pattern} madness-${i % 20}`}
            />
          ))}
          {/* Emojis */}
          {Array.from({ length: 15 }, (_, i) => (
            <div 
              key={`emoji-${i}`}
              className={`madness-emoji emoji-${currentTheme.pattern} emoji-${i % 15}`}
            >
              {currentTheme.emojis[i % currentTheme.emojis.length]}
            </div>
          ))}
        </div>
      )}
      
      <div className="debug-header">
        <h1>🐱 Catzee Effects Debug Page 🎲</h1>
        <a href="/" className="back-link">← Back to Game</a>
      </div>

      <div className="debug-sections">
        <section className="debug-section">
          <h2>🎨 Background Themes</h2>
          <p>Different themed backgrounds with cats and emojis flying in unique patterns. Current theme: <strong>{currentTheme?.name}</strong></p>
          <div className="debug-buttons">
            {backgroundThemes && Object.entries(backgroundThemes).map(([themeKey, theme]) => (
              <div key={themeKey} className="debug-button-group">
                <button 
                  className={`debug-button theme-button ${backgroundTheme === themeKey ? 'active' : ''}`}
                  onClick={() => {
                    setBackgroundTheme?.(themeKey);
                    playMeowHappy();
                  }}
                  style={{ 
                    background: theme.backgroundColor,
                    border: backgroundTheme === themeKey ? '3px solid #fff' : '1px solid #ccc',
                    color: '#fff',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                  }}
                >
                  {theme.emojis[0]} {theme.name}
                </button>
                <span className="debug-description">
                  Cats: {theme.cats.length} types, Emojis: {theme.emojis.join(' ')}, Pattern: {theme.pattern}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>Basic Effect Overlays</h2>
          <p>Core effect overlays that appear during gameplay.</p>
          <div className="debug-buttons">
            {effectButtons.map((button, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button effect-button"
                  onClick={() => {
                    if (button.sound) button.sound();
                    triggerEffect(button.effect);
                  }}
                >
                  {button.name}
                </button>
                <span className="debug-description">{button.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>🏰 Full House Effects</h2>
          <p>All full house animation variants - massive celebration effects!</p>
          <div className="debug-buttons">
            {fullHouseButtons.map((button, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button fullhouse-button"
                  onClick={() => {
                    if (button.sound) button.sound();
                    triggerEffect(button.effect);
                  }}
                >
                  {button.name}
                </button>
                <span className="debug-description">{button.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>🏎️ Small Straight Effects</h2>
          <p>Small straight animation variants (4 consecutive dice).</p>
          <div className="debug-buttons">
            {smallStraightButtons.map((button, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button small-straight-button"
                  onClick={() => {
                    if (button.sound) button.sound();
                    triggerEffect(button.effect);
                  }}
                >
                  {button.name}
                </button>
                <span className="debug-description">{button.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>🚀 Large Straight Effects</h2>
          <p>Large straight animation variants (5 consecutive dice).</p>
          <div className="debug-buttons">
            {largeStraightButtons.map((button, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button large-straight-button"
                  onClick={() => {
                    if (button.sound) button.sound();
                    triggerEffect(button.effect);
                  }}
                >
                  {button.name}
                </button>
                <span className="debug-description">{button.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>🎖️ Three of a Kind Effects</h2>
          <p>Three of a kind animation variants - triple cat chaos!</p>
          <div className="debug-buttons">
            {threeOfAKindButtons.map((button, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button three-kind-button"
                  onClick={() => {
                    if (button.sound) button.sound();
                    triggerEffect(button.effect);
                  }}
                >
                  {button.name}
                </button>
                <span className="debug-description">{button.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>💎 Four of a Kind Effects</h2>
          <p>Four of a kind animation variants - quadruple cat supremacy!</p>
          <div className="debug-buttons">
            {fourOfAKindButtons.map((button, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button four-kind-button"
                  onClick={() => {
                    if (button.sound) button.sound();
                    triggerEffect(button.effect);
                  }}
                >
                  {button.name}
                </button>
                <span className="debug-description">{button.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>Screen Effects</h2>
          <p>These effects are applied to the entire screen/body element.</p>
          <div className="debug-buttons">
            {screenEffects.map((effect, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button screen-button"
                  onClick={() => triggerScreenEffect(effect.className)}
                >
                  {effect.name}
                </button>
                <span className="debug-description">{effect.description}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>Particle Effects</h2>
          <p>Dynamic particle and explosion effects.</p>
          <div className="debug-buttons">
            <div className="debug-button-group">
              <button 
                className="debug-button particle-button"
                onClick={triggerExplosion}
              >
                Cat Explosion
              </button>
              <span className="debug-description">Flying cat explosion effect</span>
            </div>
          </div>
        </section>

        <section className="debug-section">
          <h2>Audio Effects</h2>
          <p>Test all the cat sound effects used in the game.</p>
          <div className="debug-buttons">
            {audioEffects.map((audio, index) => (
              <div key={index} className="debug-button-group">
                <button 
                  className="debug-button audio-button"
                  onClick={audio.sound}
                >
                  {audio.name}
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="debug-section">
          <h2>Combined Effects</h2>
          <p>Test combinations of effects as they occur in gameplay.</p>
          <div className="debug-buttons">
            <div className="debug-button-group">
              <button 
                className="debug-button combo-button"
                onClick={() => {
                  playMeowExcited();
                  triggerScreenEffect('mega-screen-shake');
                  triggerExplosion();
                  setTimeout(() => {
                    triggerEffect({ type: 'yahtzee' });
                  }, 1000);
                }}
              >
                Yahtzee Combo
              </button>
              <span className="debug-description">Full yahtzee celebration sequence</span>
            </div>
            <div className="debug-button-group">
              <button 
                className="debug-button combo-button"
                onClick={() => {
                  playPurrRoll();
                  triggerScreenEffect('screen-shake');
                  triggerScreenEffect('roll-chaos');
                }}
              >
                Dice Roll Combo
              </button>
              <span className="debug-description">Standard dice roll effects</span>
            </div>
          </div>
        </section>

        <section className="debug-section">
          <h2>🎲 Dice Types & Tray Prototypes</h2>
          <p>Testing different dice styles and the best tray layout (Compact Circular).</p>
          
          {/* Dice Type 1: Classic Dots */}
          <div className="prototype-container">
            <h3>Dice Type 1: Classic Dots (Rolls: {'●'.repeat(prototype1Rolls)}{'○'.repeat(3-prototype1Rolls)})</h3>
            <div className="dice-tray-prototype prototype-1">
              <div className="dice-circle">
                {prototype1Dice.map((value, index) => (
                  <div 
                    key={index} 
                    className={`die-prototype die-${value} dice-type-classic ${prototype1Held[index] ? 'held' : ''} ${prototype1Rolls < 3 ? 'clickable' : ''}`}
                    onClick={() => toggleHold(index, prototype1Held, setPrototype1Held, prototype1Rolls)}
                  >
                    <DiceSvg value={value} type="classic" />
                  </div>
                ))}
              </div>
              <div className="roll-area">
                <button 
                  className="roll-btn-proto" 
                  onClick={() => rollPrototypeDice(prototype1Dice, prototype1Held, setPrototype1Dice, setPrototype1Rolls, prototype1Rolls)}
                  disabled={prototype1Rolls === 0}
                >
                  {prototype1Rolls > 0 ? `Roll ${'🎲'.repeat(prototype1Rolls)}` : 'No Rolls Left'}
                </button>
                <button 
                  className="roll-btn-proto secondary" 
                  onClick={() => resetPrototype(setPrototype1Dice, setPrototype1Held, setPrototype1Rolls, [3, 1, 4, 6, 2], [false, true, false, true, false])}
                  style={{marginLeft: '10px'}}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Dice Type 2: Modern Pips */}
          <div className="prototype-container">
            <h3>Dice Type 2: Modern Pips (Rolls: {'●'.repeat(prototype2Rolls)}{'○'.repeat(3-prototype2Rolls)})</h3>
            <div className="dice-tray-prototype prototype-1">
              <div className="dice-circle">
                {prototype2Dice.map((value, index) => (
                  <div 
                    key={index} 
                    className={`die-prototype die-${value} dice-type-modern ${prototype2Held[index] ? 'held' : ''} ${prototype2Rolls < 3 ? 'clickable' : ''}`}
                    onClick={() => toggleHold(index, prototype2Held, setPrototype2Held, prototype2Rolls)}
                  >
                    <DiceSvg value={value} type="modern" />
                  </div>
                ))}
              </div>
              <div className="roll-area">
                <button 
                  className="roll-btn-proto" 
                  onClick={() => rollPrototypeDice(prototype2Dice, prototype2Held, setPrototype2Dice, setPrototype2Rolls, prototype2Rolls)}
                  disabled={prototype2Rolls === 0}
                >
                  {prototype2Rolls > 0 ? `Roll ${'🎲'.repeat(prototype2Rolls)}` : 'No Rolls Left'}
                </button>
                <button 
                  className="roll-btn-proto secondary" 
                  onClick={() => resetPrototype(setPrototype2Dice, setPrototype2Held, setPrototype2Rolls, [3, 1, 4, 6, 2], [false, true, false, true, false])}
                  style={{marginLeft: '10px'}}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Dice Type 3: Neon Style */}
          <div className="prototype-container">
            <h3>Dice Type 3: Neon Glow (Rolls: {'●'.repeat(prototype3Rolls)}{'○'.repeat(3-prototype3Rolls)})</h3>
            <div className="dice-tray-prototype prototype-1">
              <div className="dice-circle">
                {prototype3Dice.map((value, index) => (
                  <div 
                    key={index} 
                    className={`die-prototype die-${value} dice-type-neon ${prototype3Held[index] ? 'held' : ''} ${prototype3Rolls < 3 ? 'clickable' : ''}`}
                    onClick={() => toggleHold(index, prototype3Held, setPrototype3Held, prototype3Rolls)}
                  >
                    <DiceSvg value={value} type="neon" />
                  </div>
                ))}
              </div>
              <div className="roll-area">
                <button 
                  className="roll-btn-proto" 
                  onClick={() => rollPrototypeDice(prototype3Dice, prototype3Held, setPrototype3Dice, setPrototype3Rolls, prototype3Rolls)}
                  disabled={prototype3Rolls === 0}
                >
                  {prototype3Rolls > 0 ? `Roll ${'🎲'.repeat(prototype3Rolls)}` : 'No Rolls Left'}
                </button>
                <button 
                  className="roll-btn-proto secondary" 
                  onClick={() => resetPrototype(setPrototype3Dice, setPrototype3Held, setPrototype3Rolls, [3, 1, 4, 6, 2], [false, true, false, true, false])}
                  style={{marginLeft: '10px'}}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Prototype 2: Linear with Slots */}
          <div className="prototype-container">
            <h3>Prototype 2: Linear with Slots (Rolls: {'●'.repeat(linearRolls)}{'○'.repeat(3-linearRolls)})</h3>
            <div className="dice-tray-prototype prototype-2">
              <div className="dice-slots">
                {linearDice.map((value, index) => (
                  <div key={index} className="dice-slot">
                    <div className="slot-label">Die {index + 1}</div>
                    <div 
                      className={`die-prototype ${value ? `die-${value} dice-type-classic` : 'die-empty'} ${linearHeld[index] ? 'held' : ''} ${linearRolls < 3 && value ? 'clickable' : ''}`}
                      onClick={() => value && toggleHold(index, linearHeld, setLinearHeld, linearRolls)}
                    >
                      {value ? (
                        <DiceSvg value={value} type="classic" />
                      ) : (
                        <div className="empty-indicator">💤</div>
                      )}
                    </div>
                    <div className="hold-indicator">
                      {!value ? 'EMPTY' : linearHeld[index] ? 'HELD' : 'CLICK'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="roll-area">
                <button 
                  className="roll-btn-proto" 
                  onClick={() => rollPrototypeDice(linearDice, linearHeld, setLinearDice, setLinearRolls, linearRolls)}
                  disabled={linearRolls === 0}
                >
                  {linearRolls > 0 ? `🎲 ROLL DICE ${'🎲'.repeat(linearRolls-1)}` : 'NO ROLLS LEFT'}
                </button>
                <button 
                  className="roll-btn-proto secondary" 
                  onClick={() => resetPrototype(setLinearDice, setLinearHeld, setLinearRolls, [5, null, 2, 4, 1], [true, false, false, true, false])}
                  style={{marginLeft: '10px'}}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Prototype 3: Card-style Tray */}
          <div className="prototype-container">
            <h3>Prototype 3: Card-style Tray</h3>
            <div className="dice-tray-prototype prototype-3">
              <div className="dice-tray-card">
                <div className="tray-header">
                  <span className="rolls-left">Rolls: {'●'.repeat(cardRolls)}{'○'.repeat(3-cardRolls)}</span>
                  <span className="tray-title">Dice Tray</span>
                  <span className="score-hint">Best: Full House</span>
                </div>
                <div className="dice-grid">
                  {cardDice.map((value, index) => (
                    <div 
                      key={index} 
                      className={`die-card ${cardHeld[index] ? 'held' : ''} ${cardRolls < 3 ? 'clickable' : ''}`}
                      onClick={() => toggleHold(index, cardHeld, setCardHeld, cardRolls)}
                    >
                      <div className={`die-prototype die-${value} dice-type-classic`}>
                        <DiceSvg value={value} type="classic" />
                      </div>
                      <div className="die-value">Value: {value}</div>
                    </div>
                  ))}
                </div>
                <div className="tray-actions">
                  <button 
                    className="roll-btn-proto primary" 
                    onClick={() => rollPrototypeDice(cardDice, cardHeld, setCardDice, setCardRolls, cardRolls)}
                    disabled={cardRolls === 0}
                  >
                    {cardRolls > 0 ? 'Roll Dice' : 'No Rolls Left'}
                  </button>
                  <button 
                    className="roll-btn-proto secondary"
                    onClick={() => resetPrototype(setCardDice, setCardHeld, setCardRolls, [6, 6, 4, 4, 4], [false, false, true, true, true])}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <EffectsOverlay 
        effect={currentEffect} 
        onComplete={handleEffectComplete} 
      />
    </div>
  );
};

export default DebugPage;